import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { fetchCommunityProduct } from 'lib/dealhunt/client'
import { useOwnerViewBeacon } from 'lib/owner-notes/analytics'
import { useFollows } from 'lib/owner-notes/follows'
import { appendFeed, reviewCountLine } from 'lib/community/model'
import {
  REVIEW_DELETED_DETAIL,
  REVIEW_DELETED_HEAD,
  reviewDeletedNoticeStorage,
} from 'lib/community/author-bar'
import type { CommunityProductResponse } from 'lib/community/types'
import FollowButton from 'components/owner-notes/FollowButton'
import { DeleteNoticeBox } from './discussions/DeletedNotice'
import FeedRow from './FeedRow'
import { useHelpfulMine } from './useHelpfulMine'
import feedStyles from './Feed.module.css'

/**
 * The review the author just deleted from its thread (`afterReviewDeleted`'s one-time note), taken
 * once after mount — the hub HTML is shared-cached and never carries it. `goneId` stays for the
 * visit (the list leaves that card out: a cached page can still hold it); `shown` is the note,
 * said on this product's hub only, until dismissed.
 */
export function useReviewDeletedNotice(slug: string | null): {
  goneId: string | null
  shown: boolean
  dismiss: () => void
} {
  const [goneId, setGoneId] = useState<string | null>(null)
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const notice = reviewDeletedNoticeStorage.take()
    // Only a hit sets state: a second run (StrictMode) finds the key gone and must not clear it.
    if (notice) {
      setGoneId(notice.id)
      setShown(notice.slug === null || notice.slug === slug)
    }
    // Once per mount, like the Discussions list's note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const dismiss = useCallback(() => setShown(false), [])
  return { goneId, shown, dismiss }
}

/** The hub header's Follow (`docs/product-follow.md`): state hydrates client-side, so the
 *  shared-cached HTML always says Follow (#51). */
export const ProductHubFollow: React.VFC<{ nodeId: string }> = ({ nodeId }) => (
  <FollowButton id={nodeId} follows={useFollows()} location="hub" />
)

interface ProductHubListProps {
  /** The server-rendered first page. */
  initial: CommunityProductResponse
  now: number
}

/**
 * A product's hub list: every published owner review on it, newest first — the one order, and
 * a sentiment-blind one (owner-notes ledger #54). The first page is in the HTML; "Show more"
 * reads DealHunt from the browser, like the community feed. Each row shows a review's text,
 * so the list mounts the views beacon over its `data-note-id` rows (Directive b7bd7cd4).
 */
const ProductHubList: React.VFC<ProductHubListProps> = ({ initial, now }) => {
  const [hub, setHub] = useState(initial)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  // An answer that lands after the list unmounted, or after a newer ask, is dropped.
  const seq = useRef(0)
  useEffect(
    () => () => {
      seq.current += 1
    },
    []
  )

  const slug = initial.product.slug
  const more = useCallback(async (): Promise<void> => {
    seq.current += 1
    const mine = seq.current
    setLoading(true)
    setFailed(false)
    try {
      const next = await fetchCommunityProduct(slug, hub.page + 1)
      if (seq.current !== mine) {
        return
      }
      if (!next) {
        setFailed(true)
        return
      }
      setHub(prev => appendFeed(prev, next))
    } catch {
      if (seq.current === mine) {
        setFailed(true)
      }
    } finally {
      if (seq.current === mine) {
        setLoading(false)
      }
    }
  }, [slug, hub.page])

  const deleted = useReviewDeletedNotice(slug)
  // A kind this build does not know (a newer DealHunt) is skipped, not mis-rendered; the review
  // the author just deleted is left out for the visit (the shared-cached page can still hold it).
  const items = hub.items.filter(
    item => item.kind === 'owner_review' && item.note.id.toLowerCase() !== deleted.goneId
  )
  useOwnerViewBeacon(listRef, { enabled: items.length > 0, deps: [items.length] })
  const helpfulMine = useHelpfulMine(items.map(item => item.note.id))
  const hasMore = hub.items.length < hub.total && hub.items.length > 0

  return (
    <section aria-labelledby="hub-list-title">
      {deleted.shown && (
        <DeleteNoticeBox
          head={REVIEW_DELETED_HEAD}
          detail={REVIEW_DELETED_DETAIL}
          onDismiss={deleted.dismiss}
        />
      )}
      <div className={feedStyles.bar}>
        <h2 id="hub-list-title" className={feedStyles.barTitle}>
          {'Newest first'}
        </h2>
        <span className={feedStyles.total}>{reviewCountLine(hub.total)}</span>
      </div>
      <div ref={listRef} className={clsx(feedStyles.list, { [feedStyles.listBusy]: loading })}>
        {items.map(item => (
          <FeedRow
            key={item.note.id}
            item={item}
            now={now}
            showProduct={false}
            helpfulMine={helpfulMine}
          />
        ))}
        {!items.length && (
          <p className={feedStyles.empty}>
            {'No owner reviews yet. The first one is yours to write.'}
          </p>
        )}
      </div>
      {failed && (
        <p className={feedStyles.error}>
          {'Could not load more owner reviews. '}
          <button type="button" className={feedStyles.retry} onClick={() => void more()}>
            {'Try again'}
          </button>
        </p>
      )}
      {hasMore && !failed && (
        <button
          type="button"
          className={feedStyles.more}
          disabled={loading}
          onClick={() => void more()}
        >
          {loading ? 'Loading…' : 'Show more'}
        </button>
      )}
    </section>
  )
}

export default ProductHubList
